/* ═══════════════════════════════════════════════════════════════════
   The only file that knows the backend exists.
   In the prototype this was window.storage; in production it's /api,
   proxied by Static Web Apps to the linked Function App.

   Every call returns plain data or throws an Error with a message that is
   safe to show a candidate — the API is written to never leak internals
   into `error`.
   ═══════════════════════════════════════════════════════════════════ */

async function call(path, { method = "GET", body, signal } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    signal,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });

  if (res.status === 401) throw new AuthError("Sign in required.");
  if (res.status === 403) throw new AuthError("You do not have access to recruiting.");
  if (res.status === 204) return null;

  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error || "Something went wrong. Try again.", res.status, data);
  return data;
}

export class ApiError extends Error {
  constructor(msg, status, body) { super(msg); this.status = status; this.body = body; }
}
export class AuthError extends ApiError {
  constructor(msg) { super(msg, 401); }
}

/* ── Shape translation ────────────────────────────────────────────
   Postgres speaks snake_case, React speaks camelCase. Doing this in one
   place beats sprinkling `resume_filename` through the components. */
const jobFromApi = (j) => ({
  id: j.id,
  title: j.title,
  department: j.department,
  location: j.location,
  type: j.employment_type,
  remote: j.work_setup,
  band: j.budget_band ?? null,
  status: j.status ?? "published",
  summary: j.summary,
  responsibilities: j.responsibilities ?? [],
  qualifications: j.qualifications ?? [],
  nice: j.nice_to_have ?? [],
  postedAt: j.posted_at,
  yearsRequired: j.years_required ?? 0,
  edu: j.min_education ?? "NONE",
  bachelorFields: j.bachelor_fields ?? [],
  masterReq: j.master_requirement ?? "NONE",
  masterFields: j.master_fields ?? [],
  certsRequired: j.certs_required ?? [],
  certsPreferred: j.certs_preferred ?? [],
  applicants: j.applicants ?? 0,
});

const jobToApi = (j) => ({
  id: j.id || undefined,
  title: j.title,
  department: j.department,
  location: j.location,
  employmentType: j.type,
  workSetup: j.remote,
  budgetBand: j.band,
  status: j.status,
  summary: j.summary,
  responsibilities: j.responsibilities,
  qualifications: j.qualifications,
  niceToHave: j.nice,
  yearsRequired: j.yearsRequired,
  minEducation: j.edu,
  masterRequirement: j.masterReq,
  bachelorFields: j.bachelorFields,
  masterFields: j.masterFields,
  certsRequired: j.certsRequired,
  certsPreferred: j.certsPreferred,
});

const appFromApi = (a) => ({
  id: a.id,
  reference: a.reference,
  firstName: a.first_name,
  lastName: a.last_name,
  email: a.email,
  phone: a.phone,
  country: a.country_code,
  linkedin: a.linkedin_url,
  years: a.years_experience,
  salary: a.salary_expectation_usd,
  certs: a.certs ?? [],
  notice: a.notice_period,
  authorized: a.work_authorized ? "Yes" : "No — I would need sponsorship",
  travel: a.travel_availability,
  source: a.source,
  cover: a.cover_note,
  note: a.internal_note,
  status: a.status,
  submittedAt: a.submitted_at,
  jobId: a.job_id,
  jobTitle: a.job_title,
  department: a.department,
  resumeName: a.resume_filename,
  history: a.history,
});

/* ── Public ───────────────────────────────────────────────────────── */
export const getJobs = async () => (await call("/jobs")).map(jobFromApi);
export const getFormOptions = () => call("/form-options");

// The certification catalog is the single source for the cert chips in the
// candidate form and the job editor. code = stable id, name = label shown.
export const getCertifications = () => call("/certifications");

// Public front-end config (Turnstile site key; null when bot protection is off).
export const getConfig = () => call("/config");

/**
 * Three steps, and the file never touches our API:
 *   1. ask for a 5-minute write-only SAS on a path the server chooses
 *   2. PUT the file straight to Blob
 *   3. post the application, which verifies the blob before writing a row
 * If step 3 fails the blob is orphaned; the nightly retention job is what
 * eventually collects those.
 */
export async function submitApplication(form, file) {
  const { blobPath, uploadUrl } = await call("/applications/upload-url", {
    method: "POST",
    body: { jobId: form.jobId, contentType: file.type, sizeBytes: file.size, captchaToken: form.captchaToken },
  });

  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type },
    body: file,
  });
  if (!put.ok) throw new ApiError("The resume upload failed. Check your connection and try again.", put.status);

  const { reference } = await call("/applications", {
    method: "POST",
    body: {
      jobId: form.jobId,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      country: form.country,
      linkedin: form.linkedin,
      years: form.years,
      salary: form.salary,
      certs: form.certs,
      notice: form.notice,
      workAuthorized: form.authorized === "Yes",
      travel: form.travel,
      source: form.source,
      cover: form.cover,
      blobPath,
      resumeFilename: file.name,
      resumeContentType: file.type,
      consent: true,
    },
  });
  return reference;
}

/* ── Who am I ─────────────────────────────────────────────────────
   SWA exposes the Entra principal here. No passcode, no localStorage
   flag: the gate is the platform's, and /api/admin/* is closed at the
   edge whatever this returns. */
export async function getPrincipal() {
  try {
    const res = await fetch("/.auth/me", { credentials: "same-origin" });
    if (!res.ok) return null;
    const { clientPrincipal } = await res.json();
    if (!clientPrincipal) return null;
    return {
      name: clientPrincipal.userDetails,
      roles: clientPrincipal.userRoles || [],
      isRecruiter: (clientPrincipal.userRoles || []).includes("recruiter"),
    };
  } catch { return null; }
}
export const loginUrl = (redirect = "/") => `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(redirect)}`;
export const logoutUrl = () => `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent("/")}`;

/* ── Recruiter ────────────────────────────────────────────────────── */
export async function getApplications({ jobId, status, q } = {}) {
  const p = new URLSearchParams();
  if (jobId) p.set("jobId", jobId);
  if (status) p.set("status", status);
  if (q) p.set("q", q);
  const qs = p.toString();
  return (await call(`/manage/applications${qs ? `?${qs}` : ""}`)).map(appFromApi);
}

export const getApplication = async (id) => appFromApi(await call(`/manage/applications/${id}`));

export const updateApplication = (id, { status, internalNote }) =>
  call(`/manage/applications/${id}`, { method: "PATCH", body: { status, internalNote } });

export const eraseCandidate = (email) =>
  call(`/manage/candidates/${encodeURIComponent(email)}`, { method: "DELETE" });

export const getAnalytics = (jobId) =>
  call(`/manage/analytics${jobId ? `?jobId=${encodeURIComponent(jobId)}` : ""}`);

export const getAllJobs = async () => (await call("/manage/jobs")).map(jobFromApi);

export const saveJob = async (job) => {
  const exists = !!job.id;
  const saved = await call(exists ? `/manage/jobs/${job.id}` : "/manage/jobs", {
    method: exists ? "PUT" : "POST",
    body: jobToApi(job),
  });
  return jobFromApi(saved);
};

export const deleteJob = (id) => call(`/manage/jobs/${id}`, { method: "DELETE" });

/* ── Certification catalog (recruiter-managed) ────────────────────── */
export const getCertCatalog = () => call("/manage/certifications");
export const createCertification = (c) => call("/manage/certifications", { method: "POST", body: c });
export const updateCertification = (code, c) =>
  call(`/manage/certifications/${encodeURIComponent(code)}`, { method: "PUT", body: c });
export const deleteCertification = (code) =>
  call(`/manage/certifications/${encodeURIComponent(code)}`, { method: "DELETE" });

/**
 * The SAS lives for two minutes and is fetched at click time, never held.
 * A resume URL sitting in a component's state is a resume URL sitting in a
 * heap dump.
 */
export async function downloadResume(app) {
  const { url, filename } = await call(`/manage/applications/${app.id}/resume`);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "resume";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
